/*
=============================================================================
TRAVELER MESSAGING — In-Browser Helia IPFS + Hedera Testnet Router
Geodesic Traveler House Band & Decentralized Autonomous Studio
Official Website: https://geodesictraveler.coop
License: MIT / Apache-2.0
=============================================================================
*/

import { createHelia } from 'helia';
import { strings } from '@helia/strings';
import { Client, TransferTransaction, Hbar, PrivateKey, AccountId } from '@hashgraph/sdk';
import { CID } from 'multiformats/cid';

// =============================================================================
// 1. In-Browser Helia IPFS Node Singleton & Peer Monitoring
// =============================================================================
let heliaInstance = null;
let stringsInstance = null;

async function getHeliaNode() {
    if (!heliaInstance) {
        updateHeliaStatus('Booting in-browser Helia node...', 'active');
        try {
            heliaInstance = await createHelia({ start: true });
            if (typeof heliaInstance.start === 'function') {
                await heliaInstance.start();
            }
            stringsInstance = strings(heliaInstance);

            updateHeliaStatus('Online (P2P Ready)', 'online');

            // Listen for peer connections
            if (heliaInstance.libp2p) {
                const updatePeerCount = () => {
                    try {
                        const peers = heliaInstance.libp2p.getPeers();
                        const count = peers ? peers.length : 0;
                        const peerEl = document.getElementById('peer-count-text');
                        const peerDot = document.getElementById('peers-dot');
                        if (peerEl) peerEl.innerText = count.toString();
                        if (peerDot) {
                            peerDot.className = count > 0 ? 'status-dot online' : 'status-dot';
                        }
                    } catch (e) {}
                };

                heliaInstance.libp2p.addEventListener('peer:connect', updatePeerCount);
                heliaInstance.libp2p.addEventListener('peer:disconnect', updatePeerCount);
                updatePeerCount();
            }
        } catch (err) {
            console.error('Failed to initialize Helia node:', err);
            updateHeliaStatus('Init Error: ' + err.message, 'error');
            throw err;
        }
    }
    return heliaInstance;
}

function updateHeliaStatus(text, dotClass = '') {
    const statusText = document.getElementById('helia-status-text');
    const dot = document.getElementById('helia-dot');
    if (statusText) statusText.innerText = text;
    if (dot) dot.className = `status-dot ${dotClass}`;
}

function showToast(message, duration = 4000) {
    const toast = document.getElementById('toast');
    if (!toast) return;
    toast.innerHTML = message;
    toast.classList.add('show');
    setTimeout(() => {
        toast.classList.remove('show');
    }, duration);
}

// =============================================================================
// 2. Web Crypto API: AES-256-GCM + PBKDF2 Key Derivation
// =============================================================================
async function deriveAesKey(passphrase, saltBytes) {
    const enc = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey(
        'raw',
        enc.encode(passphrase),
        { name: 'PBKDF2' },
        false,
        ['deriveKey']
    );
    return crypto.subtle.deriveKey(
        {
            name: 'PBKDF2',
            salt: saltBytes,
            iterations: 100000,
            hash: 'SHA-256'
        },
        keyMaterial,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt']
    );
}

function bufferToBase64(u8) {
    return btoa(String.fromCharCode(...u8));
}

function base64ToBuffer(b64) {
    return new Uint8Array(atob(b64).split('').map(c => c.charCodeAt(0)));
}

async function encryptMessagePayload(plaintext, passphrase, metadata = {}) {
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const key = await deriveAesKey(passphrase, salt);

    const fullPayload = {
        text: plaintext,
        sender: metadata.sender || '',
        recipient: metadata.recipient || '',
        timestamp: new Date().toISOString(),
        app: 'traveler-messaging',
        v: 2
    };

    const encodedData = new TextEncoder().encode(JSON.stringify(fullPayload));
    const ciphertextBuffer = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv: iv },
        key,
        encodedData
    );

    return JSON.stringify({
        encrypted: true,
        algo: 'AES-GCM-256',
        salt: bufferToBase64(salt),
        iv: bufferToBase64(iv),
        ciphertext: bufferToBase64(new Uint8Array(ciphertextBuffer)),
        timestamp: fullPayload.timestamp,
        version: 2
    });
}

async function decryptMessagePayload(rawString, passphrase) {
    if (!rawString) return { text: '', encrypted: false, error: 'Empty payload' };

    let obj;
    try {
        obj = JSON.parse(rawString);
    } catch (e) {
        // Legacy base64 support
        try {
            const legacyStr = atob(rawString.trim());
            const legacyObj = JSON.parse(legacyStr);
            return { text: legacyObj.text || legacyStr, encrypted: false, version: 1 };
        } catch (err) {
            return { text: rawString, encrypted: false, version: 1 };
        }
    }

    if (!obj || !obj.encrypted || !obj.ciphertext) {
        if (obj && obj.text) return { text: obj.text, encrypted: false, version: 1 };
        return { text: typeof rawString === 'string' ? rawString : JSON.stringify(rawString), encrypted: false, version: 1 };
    }

    try {
        const salt = base64ToBuffer(obj.salt);
        const iv = base64ToBuffer(obj.iv);
        const ciphertext = base64ToBuffer(obj.ciphertext);
        const key = await deriveAesKey(passphrase, salt);

        const decryptedBuf = await crypto.subtle.decrypt(
            { name: 'AES-GCM', iv: iv },
            key,
            ciphertext
        );
        const decodedJson = new TextDecoder().decode(decryptedBuf);
        try {
            const parsed = JSON.parse(decodedJson);
            return {
                text: parsed.text || decodedJson,
                sender: parsed.sender,
                timestamp: parsed.timestamp,
                encrypted: true,
                algo: obj.algo,
                rawCiphertext: obj.ciphertext,
                fullPayload: rawString
            };
        } catch (e) {
            return { text: decodedJson, encrypted: true, algo: obj.algo, rawCiphertext: obj.ciphertext, fullPayload: rawString };
        }
    } catch (decryptErr) {
        return {
            text: '',
            encrypted: true,
            error: 'Decryption key mismatch (Check channel key)',
            rawCiphertext: obj.ciphertext,
            fullPayload: rawString
        };
    }
}

// =============================================================================
// 3. Multi-Gateway IPFS Fetcher
// =============================================================================
async function fetchFromIpfsGateways(cidString) {
    const gateways = [
        `https://dweb.link/ipfs/${cidString}`,
        `https://ipfs.io/ipfs/${cidString}`,
        `https://gateway.pinata.cloud/ipfs/${cidString}`,
        `https://w3s.link/ipfs/${cidString}`,
        `https://trustless-gateway.link/ipfs/${cidString}`,
        `https://4everland.io/ipfs/${cidString}`,
        `https://storry.tv/ipfs/${cidString}`
    ];

    const promises = gateways.map(async (gwUrl) => {
        const res = await fetch(gwUrl, { signal: AbortSignal.timeout(5000) });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const text = await res.text();
        if (!text || text.trim().length === 0) throw new Error('Empty');
        return { text, gateway: new URL(gwUrl).hostname };
    });

    return await Promise.any(promises);
}

// =============================================================================
// 4. Encrypt & Seed via Helia + Submit Hedera Pointer Transaction
// =============================================================================
window.sendTravelerMessage = async function () {
    const plaintext = document.getElementById('messageInput')?.value?.trim();
    const recipientId = document.getElementById('recipientAccount')?.value?.trim();
    const senderKeyStr = document.getElementById('senderPrivateKey')?.value?.trim();
    const senderIdStr = document.getElementById('senderAccount')?.value?.trim();
    const channelKey = document.getElementById('channelKey')?.value?.trim();
    const hbarAmountStr = document.getElementById('hbarAmount')?.value?.trim() || '0.5';

    const statusBox = document.getElementById('status');
    const sendBtn = document.getElementById('btnSendMessage');
    const sendSpinner = document.getElementById('send-spinner');

    if (!plaintext || !recipientId || !senderKeyStr || !senderIdStr || !channelKey) {
        showToast('⚠️ Please fill in all fields (Sender, Private Key, Recipient, Channel Secret Key, and Message).');
        if (statusBox) {
            statusBox.className = 'pipeline-box error';
            statusBox.innerHTML = '⚠️ Error: Missing required fields (Sender, Private Key, Recipient, Channel Secret Key, or Message).';
        }
        return;
    }

    // Format validation
    if (!/^0\.0\.\d+$/.test(senderIdStr) || !/^0\.0\.\d+$/.test(recipientId)) {
        showToast('⚠️ Invalid Hedera Account format. Must be in the form 0.0.XXXXX');
        if (statusBox) {
            statusBox.className = 'pipeline-box error';
            statusBox.innerHTML = '⚠️ Invalid Hedera Account format. Must be in the form 0.0.XXXXX';
        }
        return;
    }

    const hbarAmount = parseFloat(hbarAmountStr) || 0.5;

    // Save accounts for convenience
    localStorage.setItem('traveler_sender_account', senderIdStr);
    localStorage.setItem('traveler_recipient_account', recipientId);
    localStorage.setItem('traveler_channel_key', channelKey);

    try {
        if (sendBtn) sendBtn.disabled = true;
        if (sendSpinner) sendSpinner.style.display = 'inline';

        // 1. Client-Side AES-256-GCM Encryption
        if (statusBox) {
            statusBox.className = 'pipeline-box';
            statusBox.innerHTML = '🔒 <strong>[Step 1/4]</strong> Encrypting payload locally with AES-GCM-256...';
        }

        const encryptedBlob = await encryptMessagePayload(plaintext, channelKey, {
            sender: senderIdStr,
            recipient: recipientId
        });

        // 2. Push Encrypted Payload to IPFS Locally via Helia
        if (statusBox) {
            statusBox.innerHTML = '🌐 <strong>[Step 2/4]</strong> Seeding encrypted ciphertext blob into in-browser Helia IPFS node...';
        }

        const helia = await getHeliaNode();
        const s = strings(helia);
        const cid = await s.add(encryptedBlob);
        const cidString = cid.toString();
        localStorage.setItem('traveler_payload_' + cidString, encryptedBlob);
        console.log('[Traveler Messaging] Generated Encrypted Helia IPFS CID:', cidString);

        // 3. Connect to Hedera Testnet
        if (statusBox) {
            statusBox.innerHTML = `⚡ <strong>[Step 3/4]</strong> Submitting transaction to Hedera Testnet with memo <code>${cidString}</code>...`;
        }

        const parsedKey = parseHederaPrivateKey(senderKeyStr);
        const client = Client.forTestnet();
        client.setOperator(AccountId.fromString(senderIdStr), parsedKey);

        // 4. Transfer HBAR with IPFS CID in the transaction memo
        const transaction = await new TransferTransaction()
            .addHbarTransfer(AccountId.fromString(senderIdStr), new Hbar(-hbarAmount))
            .addHbarTransfer(AccountId.fromString(recipientId), new Hbar(hbarAmount))
            .setTransactionMemo(cidString)
            .execute(client);

        if (statusBox) {
            statusBox.innerHTML = '⏳ <strong>[Step 4/4]</strong> Waiting for Hedera consensus receipt...';
        }

        const receipt = await transaction.getReceipt(client);
        const txIdStr = transaction.transactionId ? transaction.transactionId.toString() : '';

        console.log('[Traveler Messaging] Transaction Confirmed:', receipt.status.toString(), txIdStr);

        // Save last sent payload in memory for one-click copy
        window.lastSentPayloadBlob = encryptedBlob;
        window.lastSentCid = cidString;

        if (statusBox) {
            statusBox.className = 'pipeline-box success';
            statusBox.innerHTML = `
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;">
                    <strong style="color: var(--neon-green);">✓ Encrypted Message Anchored on Hedera!</strong>
                    <span class="decrypted-badge">SUCCESS</span>
                </div>
                <div style="font-size: 0.72rem; line-height: 1.6; color: var(--text-primary);">
                    • <strong>CID:</strong> <a href="https://ipfs.io/ipfs/${cidString}" target="_blank" style="color: var(--neon-amber); word-break: break-all;">${cidString}</a><br>
                    ${txIdStr ? `• <strong>Explorer:</strong> <a href="https://hashscan.io/testnet/transaction/${txIdStr}" target="_blank" style="color: var(--neon-cyan);">HashScan Tx ↗</a><br>` : ''}
                </div>
                <div style="margin-top: 10px; display: flex; gap: 8px; flex-wrap: wrap;">
                    <button class="btn-reveal" id="btnCopyCiphertext" onclick="window.copyToClipboard(window.lastSentPayloadBlob, 'btnCopyCiphertext')">
                        <span>📋 Copy Ciphertext JSON (AES-256-GCM Payload)</span>
                    </button>
                    <button class="btn-reveal btn-secondary" onclick="window.toggleSenderPayloadPreview()">
                        <span>🔍 Toggle Ciphertext JSON Preview</span>
                    </button>
                </div>
                <div id="senderPayloadPreview" style="display: none; margin-top: 10px;">
                    <div style="font-size: 0.68rem; color: var(--neon-cyan); margin-bottom: 4px; font-weight: 700;">
                        Encrypted Ciphertext JSON (AES-256-GCM Payload):
                    </div>
                    <textarea readonly style="width: 100%; height: 85px; font-size: 0.7rem; background: var(--bg-canvas); color: var(--neon-cyan); border: 1px solid var(--border-subtle); padding: 8px; border-radius: 4px;">${escapeHtml(encryptedBlob)}</textarea>
                </div>
            `;
        }

        showToast('✓ Message encrypted with AES-256, seeded to Helia, & anchored to Hedera!');
        document.getElementById('messageInput').value = '';

    } catch (err) {
        console.error('[Traveler Messaging] Error sending message:', err);
        if (statusBox) {
            statusBox.className = 'pipeline-box error';
            statusBox.innerHTML = `❌ <strong>Error:</strong> ${escapeHtml(err.message || 'Transaction failed')}`;
        }
        showToast('❌ Error: ' + err.message);
    } finally {
        if (sendBtn) sendBtn.disabled = false;
        if (sendSpinner) sendSpinner.style.display = 'none';
    }
};

window.toggleSenderPayloadPreview = function () {
    const preview = document.getElementById('senderPayloadPreview');
    if (preview) {
        preview.style.display = preview.style.display === 'none' ? 'block' : 'none';
    }
};

window.copyToClipboard = function (text, btnId) {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
        showToast('📋 Copied Ciphertext JSON (AES-256-GCM Payload) to clipboard!');
        if (btnId) {
            const btn = document.getElementById(btnId);
            if (btn) {
                const originalHtml = btn.innerHTML;
                btn.innerHTML = '<span>✓ Copied JSON!</span>';
                setTimeout(() => { btn.innerHTML = originalHtml; }, 2000);
            }
        }
    }).catch(err => {
        console.error('Clipboard copy failed:', err);
        showToast('⚠️ Could not copy automatically. Please select text manually.');
    });
};

// =============================================================================
// 5. Check & Sync Messages Function for the Receiver with Reveal Stream
// =============================================================================
window.inboxMessagesStore = [];

window.checkTravelerMessages = async function () {
    const myAccountId = document.getElementById('receiverAccount')?.value?.trim();
    const channelKey = document.getElementById('receiverChannelKey')?.value?.trim() || '';
    const autoReveal = document.getElementById('chkAutoReveal')?.checked ?? true;

    const inboxStatus = document.getElementById('inbox-status');
    const inboxCountBadge = document.getElementById('inboxCountBadge');
    const syncSpinner = document.getElementById('sync-spinner');

    if (!myAccountId) {
        showToast('⚠️ Enter your Account ID to check your inbox.');
        if (inboxStatus) {
            inboxStatus.className = 'pipeline-box error';
            inboxStatus.innerHTML = '⚠️ Enter your Account ID to check your inbox.';
        }
        return;
    }

    if (!/^0\.0\.\d+$/.test(myAccountId)) {
        showToast('⚠️ Invalid Hedera Account format. Must be in the form 0.0.XXXXX');
        return;
    }

    localStorage.setItem('traveler_receiver_account', myAccountId);
    if (channelKey) {
        localStorage.setItem('traveler_channel_key', channelKey);
    }

    if (inboxStatus) {
        inboxStatus.className = 'pipeline-box';
        inboxStatus.innerHTML = `🔄 Querying Hedera Mirror Node for transactions to <code>${myAccountId}</code>...`;
    }
    if (syncSpinner) syncSpinner.style.display = 'inline';

    try {
        const mirrorUrl = `https://testnet.mirrornode.hedera.com/api/v1/transactions?account.id=${encodeURIComponent(myAccountId)}&order=desc&limit=25`;
        const response = await fetch(mirrorUrl, {
            headers: { 'Accept': 'application/json' }
        });

        if (!response.ok) {
            throw new Error(`Mirror node HTTP status ${response.status}`);
        }

        const data = await response.json();
        const transactions = data.transactions || [];

        const helia = await getHeliaNode();
        const s = strings(helia);

        window.inboxMessagesStore = [];

        for (const tx of transactions) {
            if (tx.memo_base64) {
                let cidString = '';
                try {
                    cidString = atob(tx.memo_base64).trim();
                } catch (e) {
                    try {
                        cidString = Buffer.from(tx.memo_base64, 'base64').toString('utf-8').trim();
                    } catch (err) {}
                }

                // Check if the memo looks like an IPFS CID (v0 Qm... or v1 bafy... / bafk...)
                if (cidString.startsWith('Qm') || cidString.startsWith('bafy') || cidString.startsWith('bafk')) {
                    let rawPayload = '';
                    let resolveSource = 'Helia P2P';

                    const cachedBlob = localStorage.getItem('traveler_payload_' + cidString);
                    if (cachedBlob) {
                        rawPayload = cachedBlob;
                        resolveSource = 'Helia Local Cache';
                    } else {
                        try {
                            const parsedCid = CID.parse(cidString);
                            // Attempt retrieval from local in-browser Helia node
                            const blob = await Promise.race([
                                s.get(parsedCid),
                                new Promise((_, reject) => setTimeout(() => reject(new Error('Helia fetch timeout')), 2000))
                            ]);
                            rawPayload = blob;
                            localStorage.setItem('traveler_payload_' + cidString, blob);
                        } catch (localErr) {
                            // Fallback to racing public IPFS gateways
                            try {
                                const gwRes = await fetchFromIpfsGateways(cidString);
                                rawPayload = gwRes.text;
                                resolveSource = `IPFS (${gwRes.gateway})`;
                                localStorage.setItem('traveler_payload_' + cidString, gwRes.text);
                            } catch (gwErr) {
                                rawPayload = '';
                                resolveSource = 'DHT Propagating';
                            }
                        }
                    }

                    let decryptedResult = null;
                    let isRevealed = false;

                    if (rawPayload && autoReveal) {
                        decryptedResult = await decryptMessagePayload(rawPayload, channelKey);
                        isRevealed = !decryptedResult.error;
                    } else if (rawPayload) {
                        decryptedResult = { rawCiphertext: rawPayload, encrypted: true };
                        isRevealed = false;
                    }

                    window.inboxMessagesStore.push({
                        id: window.inboxMessagesStore.length,
                        txId: tx.transaction_id,
                        consensusTimestamp: tx.consensus_timestamp,
                        cid: cidString,
                        rawPayload: rawPayload,
                        decrypted: decryptedResult,
                        isRevealed: isRevealed,
                        resolveSource: resolveSource,
                        showManual: !rawPayload, // If raw payload isn't found yet, show the inline input box so it never disappears!
                        showCiphertext: false
                    });
                }
            }
        }

        renderInboxList();

        if (inboxCountBadge) {
            inboxCountBadge.innerText = `${window.inboxMessagesStore.length} message${window.inboxMessagesStore.length === 1 ? '' : 's'}`;
        }

        if (inboxStatus) {
            inboxStatus.className = 'pipeline-box success';
            inboxStatus.innerHTML = `✓ Inbox synchronized via Helia & Hedera Mirror Node (${window.inboxMessagesStore.length} messages found).`;
        }

    } catch (err) {
        console.error('[Traveler Messaging] Error checking messages:', err);
        if (inboxStatus) {
            inboxStatus.className = 'pipeline-box error';
            inboxStatus.innerHTML = `❌ Error syncing inbox: ${escapeHtml(err.message)}`;
        }
        showToast('❌ Error syncing inbox: ' + err.message);
    } finally {
        if (syncSpinner) syncSpinner.style.display = 'none';
    }
};

// =============================================================================
// 6. Inbox Rendering with Permanent Inline IPFS & Decryption Controls
// =============================================================================
function renderInboxList() {
    const inboxList = document.getElementById('inboxList');
    if (!inboxList) return;

    inboxList.innerHTML = '';

    if (window.inboxMessagesStore.length === 0) {
        inboxList.innerHTML = `
            <li class="inbox-empty">
                No IPFS CID memo transactions found for this account on Hedera Testnet.
            </li>
        `;
        return;
    }

    window.inboxMessagesStore.forEach((msg, idx) => {
        const li = document.createElement('li');
        li.className = 'inbox-item';
        li.id = `inbox-msg-${idx}`;

        const dateStr = formatTimestamp(msg.consensusTimestamp);
        const txFormatted = msg.txId ? msg.txId.replace(/-(\d{9})$/, '.$1') : '';

        let contentHtml = '';

        if (msg.isRevealed && msg.decrypted && msg.decrypted.text) {
            // Case 1: Successfully Decrypted & Revealed
            contentHtml = `
                <div class="inbox-payload revealed-text">
                    <div style="font-weight: 600; margin-bottom: 4px; color: var(--neon-cyan); font-size: 0.75rem;">
                        🔓 Decrypted Plaintext Payload:
                    </div>
                    <div style="font-size: 0.9rem; line-height: 1.5; white-space: pre-wrap; color: #ffffff;">${escapeHtml(msg.decrypted.text)}</div>
                    ${msg.decrypted.sender ? `<div style="font-size: 0.68rem; color: var(--text-secondary); margin-top: 6px;">Sender: <strong>${escapeHtml(msg.decrypted.sender)}</strong></div>` : ''}
                </div>
                <div style="display: flex; gap: 8px; align-items: center; margin-top: 6px; flex-wrap: wrap;">
                    <button class="btn-reveal btn-secondary" style="font-size: 0.7rem; padding: 4px 8px;" onclick="window.toggleCiphertextPreview(${idx})">
                        <span>${msg.showCiphertext ? 'Hide Ciphertext JSON' : 'View Ciphertext JSON (AES-256-GCM Payload)'}</span>
                    </button>
                    ${msg.rawPayload ? `<button class="btn-reveal btn-secondary" style="font-size: 0.7rem; padding: 4px 8px;" id="copyBtnRevealed-${idx}" onclick="window.copyToClipboard(window.inboxMessagesStore[${idx}].rawPayload, 'copyBtnRevealed-${idx}')">📋 Copy Ciphertext JSON (AES-256-GCM Payload)</button>` : ''}
                </div>
                ${msg.showCiphertext && msg.rawPayload ? `
                    <div class="manual-input-box" style="margin-top: 8px;">
                        <div style="font-size: 0.68rem; color: var(--neon-cyan); margin-bottom: 4px; font-weight: 700;">
                            Encrypted Ciphertext JSON (AES-256-GCM Payload):
                        </div>
                        <textarea readonly style="height: 65px; font-size: 0.68rem;">${escapeHtml(msg.rawPayload)}</textarea>
                    </div>
                ` : ''}
            `;
        } else if (msg.rawPayload && msg.decrypted && msg.decrypted.error) {
            // Case 2: Payload fetched, but channel key failed
            contentHtml = `
                <div class="inbox-payload" style="border-left-color: var(--neon-crimson); background: rgba(255, 0, 85, 0.06);">
                    <div style="color: #fca5a5; font-weight: 600; font-size: 0.78rem; margin-bottom: 4px;">
                        🔒 Decryption Key Mismatch
                    </div>
                    <div style="font-size: 0.74rem; color: var(--text-secondary); margin-bottom: 8px;">
                        ${escapeHtml(msg.decrypted.error)}. Please verify or enter the secret channel passphrase below:
                    </div>
                    <div style="display: flex; gap: 6px; align-items: center;">
                        <input type="password" id="custom-key-${idx}" placeholder="Enter channel secret passphrase" value="${escapeHtml(document.getElementById('receiverChannelKey')?.value || '')}" style="font-size: 0.75rem; padding: 6px 8px;">
                        <button class="btn-reveal" onclick="window.retryDecryptWithCustomKey(${idx})">
                            <span>🔓 Decrypt</span>
                        </button>
                    </div>
                </div>
            `;
        } else {
            // Case 3: Propagating or Pending IPFS fetch — PERMANENT INLINE CONTROLS
            contentHtml = `
                <div class="inbox-payload" style="border-left-color: var(--neon-amber); background: rgba(255, 183, 3, 0.05); padding: 12px;">
                    <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px;">
                        <span style="color: var(--neon-amber); font-weight: 600; font-size: 0.78rem; display: flex; align-items: center; gap: 6px;">
                            <span class="status-dot active"></span>
                            🌐 IPFS Swarm Propagating CID
                        </span>
                        <span style="font-size: 0.65rem; color: var(--text-secondary); font-family: 'JetBrains Mono', monospace;">
                            ${escapeHtml(msg.resolveSource || 'DHT Propagating')}
                        </span>
                    </div>

                    <p style="font-size: 0.74rem; color: var(--text-secondary); line-height: 1.45; margin-bottom: 10px;">
                        The IPFS network is currently propagating this CID. You can query all 7 public IPFS gateways or paste the encrypted ciphertext JSON below to decrypt immediately:
                    </p>

                    <!-- Persistent Action Buttons -->
                    <div style="display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 10px;">
                        <button class="btn-reveal" id="btnQueryGw-${idx}" onclick="window.retryIpfsFetch(${idx})">
                            <span>🔄 Query All 7 IPFS Gateways</span>
                        </button>
                        <button class="btn-reveal btn-secondary" onclick="window.toggleManualInput(${idx})">
                            <span>${msg.showManual ? '▼ Hide Manual Paste Box' : '✏️ Paste Encrypted Ciphertext'}</span>
                        </button>
                        <a href="https://ipfs.io/ipfs/${msg.cid}" target="_blank" class="cid-chip" style="display: inline-flex; align-items: center; padding: 5px 10px; font-size: 0.72rem;">
                            Open in IPFS Gateway ↗
                        </a>
                    </div>

                    <!-- Permanent Inline Manual Input Box -->
                    <div id="manual-box-${idx}" class="manual-input-box" style="display: ${msg.showManual ? 'block' : 'none'};">
                        <label style="font-size: 0.7rem; color: var(--neon-cyan); margin-bottom: 6px; display: block; font-weight: 700;">
                            Paste Encrypted Ciphertext JSON (AES-256-GCM Payload):
                        </label>
                        <textarea id="manual-text-${idx}" placeholder='{"encrypted":true,"algo":"AES-GCM-256","salt":"...","iv":"...","ciphertext":"..."}'></textarea>
                        <div style="display: flex; gap: 8px; margin-top: 8px;">
                            <button class="btn" style="padding: 6px 14px; font-size: 0.75rem;" onclick="window.submitManualPayload(${idx})">
                                🔓 Decrypt & Reveal Message
                            </button>
                            <button class="btn btn-secondary" style="padding: 6px 12px; font-size: 0.75rem;" onclick="window.toggleManualInput(${idx})">
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            `;
        }

        li.innerHTML = `
            <div class="inbox-item-header">
                <span>🕒 ${dateStr}</span>
                <span class="${msg.isRevealed ? 'decrypted-badge' : 'encrypted-badge'}">
                    ${msg.isRevealed ? '✓ Decrypted (AES-256)' : '🔒 Encrypted (AES-256)'}
                </span>
            </div>
            ${contentHtml}
            <div class="inbox-meta-row">
                <span><strong>CID:</strong> <a href="https://ipfs.io/ipfs/${msg.cid}" target="_blank" class="cid-chip">${msg.cid} ↗</a></span>
                ${txFormatted ? `<a href="https://hashscan.io/testnet/transaction/${msg.txId}" target="_blank" class="tx-link">HashScan Tx ↗</a>` : ''}
            </div>
        `;
        inboxList.appendChild(li);
    });
}

// =============================================================================
// 7. Interactive Handlers: No Prompts, Full Inline UX
// =============================================================================
window.toggleManualInput = function (idx) {
    const msg = window.inboxMessagesStore[idx];
    if (!msg) return;
    msg.showManual = !msg.showManual;
    renderInboxList();
};

window.toggleCiphertextPreview = function (idx) {
    const msg = window.inboxMessagesStore[idx];
    if (!msg) return;
    msg.showCiphertext = !msg.showCiphertext;
    renderInboxList();
};

window.submitManualPayload = async function (idx) {
    const msg = window.inboxMessagesStore[idx];
    if (!msg) return;

    const textarea = document.getElementById(`manual-text-${idx}`);
    const rawVal = textarea ? textarea.value.trim() : '';

    if (!rawVal) {
        showToast('⚠️ Please paste the encrypted ciphertext JSON into the box.');
        return;
    }

    const channelKey = document.getElementById('receiverChannelKey')?.value?.trim() || '';

    msg.rawPayload = rawVal;
    localStorage.setItem('traveler_payload_' + msg.cid, rawVal);

    const decrypted = await decryptMessagePayload(rawVal, channelKey);
    msg.decrypted = decrypted;
    msg.isRevealed = !decrypted.error;

    if (msg.isRevealed) {
        showToast('✓ Ciphertext decrypted and message revealed!');
    } else {
        showToast('⚠️ Payload saved, but decryption failed: ' + decrypted.error);
    }

    renderInboxList();
};

window.retryIpfsFetch = async function (idx) {
    const msg = window.inboxMessagesStore[idx];
    if (!msg) return;

    const btn = document.getElementById(`btnQueryGw-${idx}`);
    if (btn) btn.innerHTML = '<span>⏳ Querying 7 Gateways...</span>';

    showToast('🌐 Concurrently querying Helia P2P node and 7 IPFS gateways...');

    try {
        // Try Helia node
        const helia = await getHeliaNode();
        const s = strings(helia);
        const parsedCid = CID.parse(msg.cid);

        try {
            const blob = await Promise.race([
                s.get(parsedCid),
                new Promise((_, rej) => setTimeout(() => rej(new Error('timeout')), 2000))
            ]);
            msg.rawPayload = blob;
            msg.resolveSource = 'Helia P2P Swarm';
            localStorage.setItem('traveler_payload_' + msg.cid, blob);
        } catch (heliaErr) {
            // Try Gateways
            const gwRes = await fetchFromIpfsGateways(msg.cid);
            msg.rawPayload = gwRes.text;
            msg.resolveSource = `IPFS (${gwRes.gateway})`;
            localStorage.setItem('traveler_payload_' + msg.cid, gwRes.text);
        }

        const channelKey = document.getElementById('receiverChannelKey')?.value?.trim() || '';
        const decrypted = await decryptMessagePayload(msg.rawPayload, channelKey);
        msg.decrypted = decrypted;
        msg.isRevealed = !decrypted.error;

        if (msg.isRevealed) {
            showToast('✓ Retrieved from IPFS & decrypted successfully!');
        } else {
            showToast('✓ Retrieved from IPFS! Check decryption key.');
        }

    } catch (err) {
        console.warn('IPFS fetch still pending:', err);
        msg.showManual = true; // Automatically expand the manual paste box so the user has it ready
        showToast('⚠️ IPFS DHT still propagating. You can paste the ciphertext directly in the box below.');
    } finally {
        renderInboxList();
    }
};

window.retryDecryptWithCustomKey = async function (idx) {
    const msg = window.inboxMessagesStore[idx];
    if (!msg || !msg.rawPayload) return;

    const customKeyInput = document.getElementById(`custom-key-${idx}`);
    const key = customKeyInput ? customKeyInput.value.trim() : '';

    if (!key) {
        showToast('⚠️ Please enter the channel passphrase.');
        return;
    }

    const decrypted = await decryptMessagePayload(msg.rawPayload, key);
    msg.decrypted = decrypted;
    msg.isRevealed = !decrypted.error;

    if (msg.isRevealed) {
        showToast('✓ Message decrypted with key!');
    } else {
        showToast('❌ Decryption failed: ' + decrypted.error);
    }

    renderInboxList();
};

window.revealMessage = async function (idx) {
    // Keep backward compatibility for any direct revealMessage calls
    await window.retryIpfsFetch(idx);
};

// =============================================================================
// 8. Helpers & Private Key Parsing
// =============================================================================
function formatTimestamp(consensusTimestamp) {
    if (!consensusTimestamp) return 'Recent';
    const seconds = parseFloat(consensusTimestamp);
    if (isNaN(seconds)) return consensusTimestamp;
    const date = new Date(seconds * 1000);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
}

function parseHederaPrivateKey(inputStr) {
    if (!inputStr) throw new Error('Private key string is empty');
    const clean = inputStr.trim();
    const hex = clean.startsWith('0x') ? clean.slice(2) : clean;

    // 1. If DER format (starts with 302e for ED25519 or 3030 for ECDSA)
    if (clean.startsWith('302e') || clean.startsWith('3030')) {
        try { return PrivateKey.fromStringDer(clean); } catch (e) {}
    }

    // 2. If 64 hex chars (32 bytes raw key), try ECDSA first (Hedera Portal default), then ED25519
    if (/^[0-9a-fA-F]{64}$/.test(hex)) {
        try {
            return PrivateKey.fromStringECDSA(hex);
        } catch (e) {}
        try {
            return PrivateKey.fromStringED25519(hex);
        } catch (e) {}
    }

    // 3. Robust fallbacks
    try { return PrivateKey.fromStringDer(clean); } catch (e) {}
    try { return PrivateKey.fromStringECDSA(hex); } catch (e) {}
    try { return PrivateKey.fromStringED25519(hex); } catch (e) {}
    try { return PrivateKey.fromString(clean); } catch (e) {}

    throw new Error('Unable to parse private key. Format should be raw HEX (64 chars) or DER-encoded.');
}

function escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.innerText = text;
    return div.innerHTML;
}

// =============================================================================
// 9. Initialization & UI Binding
// =============================================================================
document.addEventListener('DOMContentLoaded', () => {
    // Restore stored accounts from previous session if present
    const savedSender = localStorage.getItem('traveler_sender_account') || '';
    const savedRecipient = localStorage.getItem('traveler_recipient_account') || '';
    const savedReceiver = localStorage.getItem('traveler_receiver_account') || '';
    const savedChannelKey = localStorage.getItem('traveler_channel_key') || '';

    if (savedSender && document.getElementById('senderAccount')) {
        document.getElementById('senderAccount').value = savedSender;
    }
    if (savedRecipient && document.getElementById('recipientAccount')) {
        document.getElementById('recipientAccount').value = savedRecipient;
    }
    if (savedReceiver && document.getElementById('receiverAccount')) {
        document.getElementById('receiverAccount').value = savedReceiver;
    }
    if (savedChannelKey) {
        if (document.getElementById('channelKey')) document.getElementById('channelKey').value = savedChannelKey;
        if (document.getElementById('receiverChannelKey')) document.getElementById('receiverChannelKey').value = savedChannelKey;
    }

    // Password visibility toggle
    const togglePwBtn = document.getElementById('btnTogglePw');
    const pwInput = document.getElementById('senderPrivateKey');
    if (togglePwBtn && pwInput) {
        togglePwBtn.addEventListener('click', () => {
            const isPw = pwInput.type === 'password';
            pwInput.type = isPw ? 'text' : 'password';
            togglePwBtn.innerText = isPw ? '🔒' : '👁';
        });
    }

    // Auto-boot Helia node in background
    getHeliaNode().catch(e => console.warn('Background Helia boot:', e));
});
